import React, { useState, useEffect, useCallback } from 'react';
import API from '../../utils/api';
import { getImageUrl, getPlaceholderUrl } from '../../utils/imageUrl';
import {
  FiUsers, FiTool, FiSettings,
  FiSearch, FiFilter, FiEye, FiClock, FiPackage, FiX
} from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import { toast } from 'react-toastify';

const Management = () => {
  const [activeTab, setActiveTab] = useState('services');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showStockModal, setShowStockModal] = useState(false);
  const [serviceToUpdate, setServiceToUpdate] = useState(null);
  const [stockValue, setStockValue] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [serviceToUpload, setServiceToUpload] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showServiceDetailModal, setShowServiceDetailModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let endpoint = '';

      switch (activeTab) {
        case 'services':
          endpoint = '/api/services';
          break;
        case 'mechanics':
          endpoint = '/api/mechanics';
          break;
        case 'customers':
          endpoint = '/api/dashboard/customer-activity';
          break;
        default:
          endpoint = '/api/services';
      }

      // Add search and filters to the request
      const params = { search: searchQuery, ...filters };

      const response = await API.get(endpoint, { params });
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${activeTab} data:`, err);
      setError(`Failed to load ${activeTab} data. Please try again later.`);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      let endpoint = '';

      switch (activeTab) {
        case 'services':
          endpoint = `/api/services/${itemToDelete.id}`;
          break;
        case 'mechanics':
          endpoint = `/api/mechanics/${itemToDelete.id}`;
          break;
        case 'customers':
          endpoint = `/api/customers/${itemToDelete.id}`;
          break;
        default:
          endpoint = `/api/services/${itemToDelete.id}`;
      }

      await API.delete(endpoint);

      // Refresh data after deletion
      fetchData();

      // Close modal
      setShowDeleteModal(false);
      setItemToDelete(null);

      toast.success(`${activeTab === 'services' ? 'Layanan' : activeTab === 'mechanics' ? 'Montir' : 'Pelanggan'} berhasil dihapus`);
    } catch (err) {
      console.error(`Error deleting ${activeTab} item:`, err);
      setError(`Failed to delete ${activeTab} item. Please try again later.`);
    }
  };

  const handleUpdateStock = async () => {
    if (!serviceToUpdate) return;

    try {
      setLoading(true);

      await API.patch(`/api/services/${serviceToUpdate.id}/stock`, { stock: stockValue });

      // Refresh data after update
      fetchData();

      // Close modal
      setShowStockModal(false);
      setServiceToUpdate(null);

      toast.success('Stok layanan berhasil diperbarui');
    } catch (err) {
      console.error('Error updating service stock:', err);
      setError('Failed to update service stock. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleImageUpload = async () => {
    if (!serviceToUpload || !selectedImage) return;

    try {
      setUploadLoading(true);

      const formData = new FormData();
      formData.append('image', selectedImage);

      await API.put(`/api/services/${serviceToUpload.id}/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Refresh data after upload
      fetchData();

      // Close modal
      setShowImageModal(false);
      setServiceToUpload(null);
      setSelectedImage(null);

      toast.success('Gambar layanan berhasil diperbarui');
    } catch (err) {
      console.error('Error uploading service image:', err);
      toast.error('Gagal mengupload gambar layanan');
    } finally {
      setUploadLoading(false);
    }
  };

  const openServiceDetailModal = (service) => {
    setSelectedService(service);
    setShowServiceDetailModal(true);
  };

  const closeServiceDetailModal = () => {
    setShowServiceDetailModal(false);
    setSelectedService(null);
  };

  const renderServicesTab = () => {
    return (
      <div className="mb-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-500">{error}</div>
        ) : data.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow-md">
            <FiSettings size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">Tidak ada layanan yang ditemukan</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.isArray(data) && data.map((service) => (
              <div key={service.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-48 overflow-hidden relative">
                  <img
                    src={service.image_url ?
                      getImageUrl(service.image_url) :
                      getPlaceholderUrl('Layanan+Bengkel', 400, 200)
                    }
                    alt={service.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getPlaceholderUrl('Gambar+Tidak+Tersedia', 400, 200);
                    }}
                  />
                  <div className="absolute top-0 right-0 bg-primary text-white px-3 py-1 m-2 rounded-full text-sm font-medium">
                    Rp {service.price ? service.price.toLocaleString('id-ID') : '0'}
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-semibold text-secondary">{service.name}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${service.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {service.in_stock ? `Tersedia` : 'Tidak Tersedia'}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{service.description}</p>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center text-gray-700">
                      <FiClock className="mr-1" />
                      <span className="text-sm">{service.estimated_time || 0} menit</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <FiPackage className="mr-1" />
                      <span className="text-sm">Stok: {service.stock || 0}</span>
                    </div>
                  </div>
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {service.category}
                    </span>
                    <div className="flex items-center">
                      <button
                        className="text-blue-500 hover:text-blue-700 flex items-center"
                        onClick={() => openServiceDetailModal(service)}
                      >
                        <FiEye size={18} className="mr-1" /> Detail
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMechanicsTab = () => {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3">Nama</th>
              <th scope="col" className="px-6 py-3">Pengalaman</th>
              <th scope="col" className="px-6 py-3">Rating</th>
              <th scope="col" className="px-6 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(data) && data.map((mechanic) => (
              <tr key={mechanic.id} className="bg-white border-b hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">
                  {mechanic.name}
                </td>
                <td className="px-6 py-4">
                  {mechanic.experience} tahun
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <span className="mr-1">{mechanic.rating}</span>
                    <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${mechanic.status === 'active' ? 'bg-green-100 text-green-800' :
                      mechanic.status === 'on-leave' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                    }`}>
                    {mechanic.status === 'active' ? 'Aktif' :
                      mechanic.status === 'on-leave' ? 'Cuti' :
                        'Tidak Aktif'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCustomersTab = () => {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3">Nama</th>
              <th scope="col" className="px-6 py-3">Email</th>
              <th scope="col" className="px-6 py-3">Telepon</th>
              <th scope="col" className="px-6 py-3">Total Booking</th>
              <th scope="col" className="px-6 py-3">Total Pengeluaran</th>
              <th scope="col" className="px-6 py-3">Terakhir Kunjungan</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(data) && data.map((customer) => (
              <tr key={customer.id} className="bg-white border-b hover:bg-gray-50">
                <td className="px-6 py-4 font-medium text-gray-900">
                  {customer.name}
                </td>
                <td className="px-6 py-4">
                  {customer.email}
                </td>
                <td className="px-6 py-4">
                  {customer.phone || '-'}
                </td>
                <td className="px-6 py-4">
                  {customer.total_bookings || 0}
                </td>
                <td className="px-6 py-4">
                  {customer.total_spent ? `Rp ${parseFloat(customer.total_spent).toLocaleString('id-ID')}` : 'Rp 0'}
                </td>
                <td className="px-6 py-4">
                  {customer.last_visit ? new Date(customer.last_visit).toLocaleDateString('id-ID') : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderServiceDetailModal = () => {
    if (!selectedService) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-secondary">Detail Layanan</h2>
            <button
              onClick={closeServiceDetailModal}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <FiX size={24} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="h-64 overflow-hidden rounded-lg mb-4">
                <img
                  src={selectedService.image_url ?
                    getImageUrl(selectedService.image_url) :
                    getPlaceholderUrl('Layanan+Bengkel', 400, 300)
                  }
                  alt={selectedService.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = getPlaceholderUrl('Gambar+Tidak+Tersedia', 400, 300);
                  }}
                />
              </div>
              <div className="mb-4">
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  {selectedService.category}
                </span>
                <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${selectedService.in_stock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {selectedService.in_stock ? `Tersedia (${selectedService.stock || 0})` : 'Tidak Tersedia'}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{selectedService.name}</h3>
              <div className="text-primary font-semibold text-lg mb-4">
                Rp {selectedService.price ? selectedService.price.toLocaleString('id-ID') : '0'}
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-700 mb-1">Deskripsi:</h4>
                <p className="text-gray-600">{selectedService.description || 'Tidak ada deskripsi'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">Estimasi Waktu:</h4>
                  <div className="flex items-center text-gray-600">
                    <FiClock className="mr-1" />
                    <span>{selectedService.estimated_time || 0} menit</span>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">Stok:</h4>
                  <div className="flex items-center text-gray-600">
                    <FiPackage className="mr-1" />
                    <span>{selectedService.stock || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t flex justify-end">
            <button
              onClick={closeServiceDetailModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Tutup
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout userRole="owner" userName="Owner">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Manajemen</h1>

        {/* Tab navigation */}
        <div className="flex border-b mb-6">
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'services' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('services')}
          >
            <FiSettings className="inline mr-2" />
            Layanan
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'mechanics' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('mechanics')}
          >
            <FiTool className="inline mr-2" />
            Montir
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'customers' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('customers')}
          >
            <FiUsers className="inline mr-2" />
            Pelanggan
          </button>
        </div>

        {/* Services tab header */}
        {activeTab === 'services' && (
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-secondary">Layanan Bengkel</h1>
              <p className="text-gray-600">Daftar semua layanan yang tersedia di bengkel</p>
            </div>
          </div>
        )}

        {/* Search and filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {activeTab === 'services' ? (
            <div className="bg-white p-4 rounded-lg shadow-md w-full">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <form onSubmit={(e) => { e.preventDefault(); fetchData(); }} className="flex mb-4 md:mb-0">
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiSearch className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="pl-10 pr-4 py-2 w-full border rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                      placeholder="Cari layanan..."
                      value={searchQuery}
                      onChange={handleSearch}
                    />
                  </div>
                  <button
                    type="submit"
                    className="bg-primary text-white px-4 py-2 rounded-r hover:bg-primary-dark"
                  >
                    Cari
                  </button>
                </form>

                <div className="flex items-center">
                  <span className="mr-2 text-gray-500">Kategori:</span>
                  <select
                    className="border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    defaultValue=""
                  >
                    <option value="">Semua</option>
                    <option value="Perawatan Rutin">Perawatan Rutin</option>
                    <option value="Rem & Suspensi">Rem & Suspensi</option>
                    <option value="AC & Electrical">AC & Electrical</option>
                    <option value="Mesin">Mesin</option>
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder={`Cari ${activeTab === 'mechanics' ? 'montir' : 'pelanggan'}...`}
                  value={searchQuery}
                  onChange={handleSearch}
                />
              </div>
              <div className="flex items-center">
                <FiFilter className="text-gray-400 mr-2" />
                <select
                  className="border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  defaultValue=""
                >
                  <option value="">Semua Status</option>
                  {activeTab === 'mechanics' && (
                    <>
                      <option value="active">Aktif</option>
                      <option value="on-leave">Cuti</option>
                      <option value="inactive">Tidak Aktif</option>
                    </>
                  )}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow p-6">
          {activeTab === 'services' && renderServicesTab()}
          {activeTab === 'mechanics' && renderMechanicsTab()}
          {activeTab === 'customers' && renderCustomersTab()}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Konfirmasi Hapus</h3>
            <p className="mb-6">
              Apakah Anda yakin ingin menghapus {activeTab === 'services' ? 'layanan' : activeTab === 'mechanics' ? 'montir' : 'pelanggan'} ini?
              {itemToDelete && itemToDelete.name && <span className="font-semibold block mt-2">"{itemToDelete.name}"</span>}
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 border rounded-md hover:bg-gray-100"
                onClick={() => setShowDeleteModal(false)}
              >
                Batal
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={handleDelete}
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Update Modal */}
      {showStockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Update Stok Layanan</h3>
            {serviceToUpdate && (
              <>
                <p className="mb-4">
                  Update stok untuk layanan <span className="font-semibold">"{serviceToUpdate.name}"</span>
                </p>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Jumlah Stok:</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                    value={stockValue}
                    onChange={(e) => setStockValue(parseInt(e.target.value) || 0)}
                  />
                </div>
              </>
            )}
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 border rounded-md hover:bg-gray-100"
                onClick={() => setShowStockModal(false)}
              >
                Batal
              </button>
              <button
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
                onClick={handleUpdateStock}
              >
                Update Stok
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Upload Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">Upload Gambar Layanan</h3>
            {serviceToUpload && (
              <>
                <p className="mb-4">
                  Upload gambar untuk layanan <span className="font-semibold">"{serviceToUpload.name}"</span>
                </p>
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">Pilih Gambar:</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                    onChange={handleImageChange}
                  />
                </div>
                {selectedImage && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500">Preview:</p>
                    <div className="mt-2 h-40 bg-gray-100 flex items-center justify-center">
                      <img
                        src={URL.createObjectURL(selectedImage)}
                        alt="Preview"
                        className="h-full object-contain"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 border rounded-md hover:bg-gray-100"
                onClick={() => setShowImageModal(false)}
              >
                Batal
              </button>
              <button
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:bg-gray-400"
                onClick={handleImageUpload}
                disabled={!selectedImage || uploadLoading}
              >
                {uploadLoading ? 'Mengupload...' : 'Upload Gambar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Detail Modal */}
      {showServiceDetailModal && renderServiceDetailModal()}
    </DashboardLayout>
  );
};

export default Management; 
